import { createModel } from '@rematch/core';
import type { RootModel, RootState } from '@/components/WellLoggingOperations/types';
import type {
    CreateProjectFundRequest,
    ExplorationZonesResponse,
    FieldsResponse,
    ListProjectFundsParams,
    ProjectFundUserInfoCreateRequest,
    UpdateProjectFundRequest,
} from '@/api/api.v2';
import { api } from '@/api/api.v2';
import {
    getCustomOptionsDiff,
    hasAnyOptions,
    mapToDropdownOptions,
} from '@/components/MiningAndPumping/MiniMapWidget/Toolbar/ProjectFondsSettings/utils';
import type { DropdownOptions } from '@/components/MiningAndPumping/MiniMapWidget/Toolbar/ProjectFondsSettings/types';
import { updateWells } from '@/components/MapGeneration/MapView';

export type ProjectFund = {
    id: number;
    title: string;
    securityLevelId: AccessEnum;
    createUser: string;
    changeUser: string;
    description: string;
    createDate: string;
    changeDate: string;
    ventureId: number;
    fieldId: number;
    areaId: number;
    mineId: number;
    kupolId: number;
    color: string;
};

export enum AccessEnum {
    Personal = 1,
    General = 3,
    Readonly = 2,
}

type AccessType = {
    value: AccessEnum;
    label: string;
};

type AddedPoint = {
    pressed_x: number;
    pressed_y: number;
    released_x: number;
    released_y: number;
};

export type ProjectFundState = {
    accessTypes: AccessType[];
    activeAccessTypes: AccessEnum[];
    projectFondsData: ProjectFund[];
    activeProjectFond: number | null;
    selectedProjectFonds: number[];
    searchValue: string;
    isLoading: boolean;
    optionsMap: Record<number, DropdownOptions>;
    currentExplorationZones: ExplorationZonesResponse;
    currentFields: FieldsResponse;
    isColorLoading: number[];
    mapColors: string[];
    openCreateWellModal: boolean;
    customOptionsMap: Record<number, Omit<ProjectFundUserInfoCreateRequest, 'projectFundId'>>;
    addedPoint: AddedPoint | null;
};

export type ProjectFundDispatch = {
    setActiveAccessTypes: (payload: AccessEnum[]) => void;
    setProjectFondsData: (payload: ProjectFund[]) => void;
    addProjectFondsData: (payload: ProjectFund) => void;
    updateProjectFondsData: (payload: ProjectFund) => void;
    deleteProjectFondsData: (payload: number) => void;
    setActiveProjectFond: (payload: number | null) => void;
    addOption: (payload: { key: number; options: DropdownOptions }) => void;
    setExplorationZones: (payload: ExplorationZonesResponse) => void;
    setCurrentFields: (payload: FieldsResponse) => void;
    setSearchValue: (payload: string) => void;
    setIsLoading: (payload: boolean) => void;
    setProjectColor: (payload: { projectFundId: number; color: string }) => void;
    checkProjectFond: (payload: number) => void;
    setSelectedProjectFunds: (payload: number[]) => void;
    setOpenCreateWellModal: (payload: boolean) => void;
    setColorLoading: (payload: { projectFundId: number; isLoading: boolean }) => void;
    setCustomOptions: (payload: Omit<ProjectFundUserInfoCreateRequest, 'projectFundId'>) => void;
    addCustomOption: (payload: {
        key: keyof Omit<ProjectFundUserInfoCreateRequest, 'projectFundId'>;
        value: string;
        fieldId?: number;
    }) => void;
    moveTempCustomOptions: (payload: number) => void;
    clearTempCustomOptions: () => void;
    setAddedPoint: (point: AddedPoint) => void;

    getAllProjects: (payload: ListProjectFundsParams) => Promise<void>;
    createProject: (payload: CreateProjectFundRequest) => Promise<void>;
    updateProject: (payload: UpdateProjectFundRequest) => Promise<void>;
    deleteProject: () => Promise<void>;
    getFieldsInfo: (payload: { ventureId?: number; fieldId?: number }) => Promise<void>;
    getInfo: (payload: { explorationZoneId?: number; fieldId?: number }) => Promise<void>;
    changeColor: (payload: { projectFundId: number; color: string }) => Promise<void>;
};

export const defaultProjectFundState: ProjectFundState = {
    accessTypes: [],
    activeAccessTypes: [AccessEnum.General, AccessEnum.Personal, AccessEnum.Readonly],
    projectFondsData: [],
    activeProjectFond: null,
    selectedProjectFonds: [],
    searchValue: '',
    optionsMap: {},
    isLoading: false,
    currentFields: {
        fields: [],
    },
    currentExplorationZones: {
        explorationZones: [],
    },
    isColorLoading: [],
    mapColors: [],
    openCreateWellModal: false,
    customOptionsMap: {
        0: {},
    },
    addedPoint: null,
};

export const projectFundModel = createModel<RootModel>()({
    name: 'projectFund',
    state: {
        ...defaultProjectFundState,
    },
    reducers: {
        setActiveAccessTypes(state, payload: AccessEnum[]) {
            return { ...state, activeAccessTypes: payload };
        },
        setProjectFondsData(state, payload: ProjectFund[]) {
            return { ...state, projectFondsData: payload };
        },
        addProjectFondsData(state, payload: ProjectFund) {
            return { ...state, projectFondsData: [...state.projectFondsData, payload] };
        },
        updateProjectFondsData(state, payload: ProjectFund) {
            return {
                ...state,
                projectFondsData: state.projectFondsData.map(project =>
                    project.id === payload.id ? { ...project, ...payload } : project,
                ),
            };
        },
        deleteProjectFondsData(state, payload: number) {
            return { ...state, projectFondsData: state.projectFondsData.filter(({ id }) => payload !== id) };
        },
        setActiveProjectFond(state, payload: number | null) {
            return {
                ...state,
                activeProjectFond: payload,
            };
        },
        addOption(state, payload: { key: number; options: DropdownOptions }) {
            return { ...state, optionsMap: { ...state.optionsMap, [payload.key]: payload.options } };
        },
        setExplorationZones(state, payload: ExplorationZonesResponse) {
            return { ...state, currentExplorationZones: payload };
        },
        setCurrentFields(state, payload: FieldsResponse) {
            return { ...state, currentFields: payload };
        },
        setSearchValue(state, payload: string) {
            return { ...state, searchValue: payload };
        },
        setIsLoading(state, payload: boolean) {
            return { ...state, isLoading: payload };
        },
        setProjectColor(state, payload: { projectFundId: number; color: string }) {
            return {
                ...state,
                projectFondsData: state.projectFondsData.map(project =>
                    project.id === payload.projectFundId ? { ...project, color: payload.color } : project,
                ),
            };
        },
        setSelectedProjectFunds(state, payload: number[]) {
            return {
                ...state,
                selectedProjectFonds: payload,
            };
        },
        checkProjectFond(state, payload: number) {
            if (state.selectedProjectFonds.includes(payload)) {
                return {
                    ...state,
                    selectedProjectFonds: state.selectedProjectFonds.filter(selected => selected !== payload),
                };
            } else {
                return { ...state, selectedProjectFonds: [...state.selectedProjectFonds, payload] };
            }
        },
        setColorLoading(state, payload: { projectFundId: number; isLoading: boolean }) {
            const { projectFundId, isLoading } = payload;
            return {
                ...state,
                isColorLoading: isLoading
                    ? [...state.isColorLoading, projectFundId]
                    : state.isColorLoading.filter(projectId => projectFundId !== projectId),
            };
        },
        setOpenCreateWellModal(state, payload: boolean) {
            return { ...state, openCreateWellModal: payload };
        },
        setCustomOptions(state, payload: Omit<ProjectFundUserInfoCreateRequest, 'projectFundId'>) {
            const id = state.activeProjectFond ?? 0;
            return {
                ...state,
                customOptionsMap: {
                    ...state.customOptionsMap,
                    [id]: payload,
                },
            };
        },
        addCustomOption(
            state,
            payload: {
                key: keyof Omit<ProjectFundUserInfoCreateRequest, 'projectFundId'>;
                value: string;
                fieldId?: number;
            },
        ) {
            const id = state.activeProjectFond ?? 0;
            const prev = state.customOptionsMap[id] ?? {};
            const prevArray = prev[payload.key] ?? [];
            const newItem = payload.fieldId
                ? { value: payload.value, fieldId: payload.fieldId }
                : { value: payload.value };

            return {
                ...state,
                customOptionsMap: {
                    ...state.customOptionsMap,
                    [id]: {
                        ...prev,
                        [payload.key]: [...prevArray, newItem],
                    },
                },
            };
        },
        moveTempCustomOptions(state, payload: number) {
            const temp = state.customOptionsMap[0];
            if (!temp) return state;

            const newMap = { ...state.customOptionsMap };
            delete newMap[0];
            newMap[payload] = temp;

            return { ...state, customOptionsMap: newMap };
        },
        clearTempCustomOptions(state) {
            const newMap = { ...state.customOptionsMap };
            delete newMap[0];
            return { ...state, customOptionsMap: newMap };
        },
        setAddedPoint(state, payload: AddedPoint) {
            return { ...state, addedPoint: payload };
        },
    },

    //@ts-ignore
    effects: () => ({
        async getAllProjects(payload: ListProjectFundsParams) {
            this.setIsLoading(true);
            const projectsList = await api.map.projectFund.list(payload);
            this.setProjectFondsData(projectsList.projectFund);
            this.setIsLoading(false);
        },
        async createProject(projectFundData: CreateProjectFundRequest, rootState: RootState) {
            this.setIsLoading(true);
            const { projectFund } = await api.map.projectFund.create(projectFundData);

            this.moveTempCustomOptions(projectFund.id);

            const customOptions = rootState.projectFund.customOptionsMap[0];
            if (customOptions) {
                await api.map.projectFund.createCustomOptions({
                    projectFundId: projectFund.id,
                    ...customOptions,
                });
                this.clearTempCustomOptions();
            }
            this.addProjectFondsData(projectFund);
            const projectIds = rootState.projectFund.projectFondsData.map(({ id }) => id);
            await updateWells(projectIds);
            this.setIsLoading(false);
        },
        async updateProject(projectFundData: UpdateProjectFundRequest, rootState: RootState) {
            const res = await api.map.projectFund.update(projectFundData);

            const customOptions = rootState.projectFund.customOptionsMap[projectFundData.id];
            if (customOptions) {
                const existingOptions = await api.map.projectFund.getCustomOptions({
                    projectFundId: projectFundData.id,
                });

                const diffOptions = getCustomOptionsDiff(existingOptions, customOptions);

                if (hasAnyOptions(diffOptions)) {
                    await api.map.projectFund.createCustomOptions({
                        projectFundId: projectFundData.id,
                        ...diffOptions,
                    });
                }
            }

            if (res) {
                const { deletedProjectFundWellIds, changedProjectFundWells, addedProjectFundWells, ...updatedProject } =
                    projectFundData;
                this.updateProjectFondsData({ ...updatedProject, securityLevelId: updatedProject.securityLevel });
            }
            const projectIds = rootState.projectFund.projectFondsData.map(({ id }) => id);
            await updateWells(projectIds);
        },
        async deleteProject(_, rootState: RootState) {
            this.setIsLoading(true);
            const { activeProjectFond } = rootState.projectFund;
            const { deletedProjectFundId } = await api.map.projectFund.delete(activeProjectFond);
            this.deleteProjectFondsData(deletedProjectFundId);
            this.setIsLoading(false);
            const projectIds = rootState.projectFund.projectFondsData.map(({ id }) => id);
            await updateWells(projectIds);
        },
        async getFieldsInfo(payload: { ventureId?: number; fieldId?: number }) {
            const currentFields = await api.map.projectFund.dropdownFields(payload);
            const currentExplorationZones = await api.map.projectFund.dropdownExplorationZones(payload);
            this.setCurrentFields(currentFields);
            this.setExplorationZones(currentExplorationZones);
        },
        async getInfo(payload: { explorationZoneId?: number; fieldId?: number }, rootState) {
            const { explorationZoneId, fieldId } = payload;
            const {
                projectFund: { optionsMap },
            } = rootState as unknown as RootState;
            const key = fieldId ?? explorationZoneId;
            if (!optionsMap[key!]) {
                const info = await api.map.projectFund.info(fieldId ? { fieldId } : payload);
                this.addOption({ key, options: mapToDropdownOptions(info) });
            }
        },
        async changeColor(payload: { projectFundId: number; color: string }, rootState: RootState) {
            const { projectFundId, color } = payload;
            this.setColorLoading({ projectFundId, isLoading: true });
            const res = await api.map.projectFund.setColor(projectFundId, { color });
            if (res) {
                this.setProjectColor(payload);
            }
            const projectIds = rootState.projectFund.projectFondsData.map(({ id }) => id);
            await updateWells(projectIds);
            this.setColorLoading({ projectFundId, isLoading: false });
        },
    }),
});
